import React from 'react';
import SliderWithInput from '../../ChartOptions/SliderWithInput';
import { OrientationRadioGroup } from '../../ChartOptions/Legend/LegendComponents/OrientationRadioGroup';
import { FONT_WEIGHT_OPTIONS, FONT_STYLE_OPTIONS, SLIDER_CONFIGS } from './ButtonContent.constants';
import ColorPicker from './ColorPicker';
import AlignmentControls from './AlignmentControls';
import { TextProps, StateBasedProps } from './ButtonContent';
import { getDefaultColor, updateStateBasedProp, getStateBasedProp } from './ButtonContent.utils';

interface TextControlsProps {
  text: string | { default: string; hover?: string };
  currentTextProps: TextProps;
  activeState: 'default' | 'hover';
  updateImage: (updates: any) => void;
  textColorPickerOpen: boolean;
  textProps: TextProps | StateBasedProps<TextProps>;
  setTextColorPickerOpen: (open: boolean) => void;
  iconColor?: string;
}

const TextControls: React.FC<TextControlsProps> = ({
  text,
  currentTextProps,
  activeState,
  textProps,
  updateImage,
  textColorPickerOpen,
  setTextColorPickerOpen,
  iconColor,
}) => {
  const updateTextProps = (updates: Partial<TextProps>) => {
    const newTextProps = updateStateBasedProp(textProps, updates, activeState, 'text');
    updateImage({ textProps: newTextProps });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <div>
        <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
          Button Text
        </div>
        <input
          type="text"
          value={
            getStateBasedProp(
              typeof text === 'object' ? text : { default: text || '' },
              activeState,
            ) || ''
          }
          onChange={(e) => {
            const newTextValue = e.target.value;
            if (typeof text === 'object' || activeState === 'hover') {
              const updatedText = updateStateBasedProp(
                typeof text === 'object' ? text : { default: text || '' },
                newTextValue,
                activeState,
                undefined,
                true,
              );
              updateImage({ text: updatedText });
            } else {
              updateImage({ text: newTextValue });
            }
          }}
          style={{
            width: '95%',
            padding: '8px 10px',
            fontSize: '12px',
            border: '1px solid #ccc',
            height: '25px',
            borderRadius: '2px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div>
        <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
          Font Size
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={currentTextProps.fontSize ?? SLIDER_CONFIGS.fontSize.default}
          sliderMinMax={SLIDER_CONFIGS.fontSize}
          changeValue={(value: number) =>
            updateTextProps({
              fontSize: Math.max(
                SLIDER_CONFIGS.fontSize.min,
                Math.min(SLIDER_CONFIGS.fontSize.max, value),
              ),
            })
          }
        />
      </div>

      <ColorPicker
        label="Text Color"
        lableColor={iconColor}
        color={getDefaultColor('text', currentTextProps.color || '', activeState) as string}
        isOpen={textColorPickerOpen}
        onToggle={() => setTextColorPickerOpen(!textColorPickerOpen)}
        onColorChange={(newColor) => updateTextProps({ color: newColor })}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '5px' }}>
        <div>
          <div
            className="optionDescription"
            style={{ marginBottom: '5px', marginTop: '3px', color: iconColor }}
          >
            Font Weight
          </div>
          <OrientationRadioGroup
            orientation={currentTextProps.fontWeight || 'Normal'}
            options={FONT_WEIGHT_OPTIONS}
            onChange={(key) => updateTextProps({ fontWeight: key })}
          />
        </div>

        <div>
          <div
            className="optionDescription"
            style={{ marginBottom: '5px', marginTop: '3px', color: iconColor }}
          >
            Font Style
          </div>
          <OrientationRadioGroup
            orientation={currentTextProps.fontStyle || 'Normal'}
            options={FONT_STYLE_OPTIONS}
            onChange={(key) => updateTextProps({ fontStyle: key })}
          />
        </div>
      </div>

      <AlignmentControls
        type="text"
        currentProps={currentTextProps}
        updateProps={updateTextProps}
        iconColor={iconColor}
      />
    </div>
  );
};

export default TextControls;
